// assets/AllCourses/Exercises/CrosswordExercise.jsx

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { cn } from "@/utils/utils";

const BLACK_CELL = "#";

export default function CrosswordExercise({
  exercise = {},
  onUpdate,
  errors = {},
  isPreview = false,
  showResult = false, // ⚠️ IMPORTANT: match parent prop name
  onComplete = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= NORMALIZE DATA ================= */
  const crosswordData =
    exercise.crosswordData || { grid: [], clues: { across: [], down: [] } };

  const grid = Array.isArray(crosswordData.grid)
    ? crosswordData.grid.map(row =>
        Array.isArray(row)
          ? row.map(c => (typeof c === "string" ? c : ""))
          : []
      )
    : [];

  const gridSize = grid.length;

  const clues = {
    across: Array.isArray(crosswordData.clues?.across)
      ? crosswordData.clues.across
      : [],
    down: Array.isArray(crosswordData.clues?.down)
      ? crosswordData.clues.down
      : [],
  };

  /* ================= STATE ================= */
  const createEmptyGrid = () =>
    gridSize > 0
      ? Array.from({ length: gridSize }, () =>
          Array.from({ length: gridSize }, () => "")
        )
      : [];

  const [userGrid, setUserGrid] = useState(createEmptyGrid);
  const completedRef = useRef(false);

  /* ================= RESET ON RETRY ================= */
  useEffect(() => {
    if (!exercise?._id) return;

    setUserGrid(createEmptyGrid());
    completedRef.current = false;
  }, [exercise._id]);

  useEffect(() => {
    if (!showResult) {
      completedRef.current = false;
    }
  }, [showResult]);

  /* ================= HELPERS ================= */
  const isBlackCell = (r, c) => grid[r]?.[c] === BLACK_CELL;

  const isActiveCell = (r, c) =>
    !isBlackCell(r, c) && grid[r]?.[c] !== "";

  const getClueNumber = (r, c) =>
    clues.across.find(cl => cl.row === r && cl.col === c)?.number ||
    clues.down.find(cl => cl.row === r && cl.col === c)?.number ||
    null;

  /* ================= STUDENT INPUT ================= */
  const handleStudentChange = (r, c, value) => {
    if (isEditMode || showResult || isBlackCell(r, c)) return;

    const letter = value.slice(-1).toUpperCase();

    setUserGrid(prev => {
      const next = prev.map((row, ri) =>
        row.map((cell, ci) =>
          ri === r && ci === c ? letter : cell
        )
      );

      // 🔥 ALWAYS UPDATE PARENT (important for submit button)
      onComplete(next, false);

      const allFilled = grid.every((row, ri) =>
        row.every(
          (cell, ci) =>
            isBlackCell(ri, ci) ||
            cell === "" ||
            next[ri][ci] !== ""
        )
      );

      if (allFilled && !completedRef.current) {
        completedRef.current = true;
        checkAnswers(next);
      }

      return next;
    });
  };

  /* ================= CHECK ================= */
  const checkAnswers = currentGrid => {
    let allCorrect = true;

    for (let r = 0; r < gridSize; r++) {
      for (let c = 0; c < gridSize; c++) {
        if (!isActiveCell(r, c)) continue;

        const entered = currentGrid[r][c];
        const expected = grid[r][c];

        if (entered.toUpperCase() !== expected.toUpperCase()) {
          allCorrect = false;
        }
      }
    }

    // ✅ CORRECT FORMAT
    onComplete(currentGrid, allCorrect);
  };

  /* ================= EDIT MODE ================= */
  const updateCrossword = next => {
    if (isEditMode) onUpdate("crosswordData", next);
  };

  const createGrid = size => {
    if (!isEditMode) return;
    updateCrossword({
      grid: Array.from({ length: size }, () =>
        Array.from({ length: size }, () => "")
      ),
      clues: { across: [], down: [] },
    });
  };

  const handleCellChange = (r, c, value) => {
    if (!isEditMode) return;

    const letter =
      value === "#"
        ? BLACK_CELL
        : value.slice(0, 1).toUpperCase();

    const nextGrid = grid.map((row, ri) =>
      row.map((cell, ci) =>
        ri === r && ci === c ? letter : cell
      )
    );

    updateCrossword({ ...crosswordData, grid: nextGrid });
  };

  if (isEditMode) {
    return (
      <div className="space-y-10">
        {gridSize === 0 && (
          <div className="text-center py-10">
            <Button onClick={() => createGrid(10)}>
              Create 10×10 Grid
            </Button>
          </div>
        )}

        {gridSize > 0 && (
          <div className="flex justify-center">
            <div
              className="grid gap-1 bg-muted/50 p-4 rounded-xl"
              style={{
                gridTemplateColumns: `repeat(${gridSize}, 48px)`,
              }}
            >
              {grid.map((row, rIdx) =>
                row.map((cell, cIdx) => (
                  <Input
                    key={`${rIdx}-${cIdx}`}
                    maxLength={1}
                    value={cell === BLACK_CELL ? "#" : cell}
                    onChange={e =>
                      handleCellChange(
                        rIdx,
                        cIdx,
                        e.target.value
                      )
                    }
                    className={cn(
                      "w-12 h-12 text-center font-bold",
                      cell === BLACK_CELL && "bg-black text-white"
                    )}
                  />
                ))
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  /* ================= STUDENT MODE ================= */
  if (gridSize === 0) {
    return (
      <Card>
        <CardHeader>
          <CardDescription>No crossword available.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="max-w-5xl mx-auto shadow-sm">
      <CardHeader>
        <CardTitle>Crossword Puzzle</CardTitle>
        <CardDescription>
          Fill in the crossword using clues.
        </CardDescription>
      </CardHeader>

      <CardContent className="grid md:grid-cols-2 gap-10">
        {/* GRID */}
        <div className="flex justify-center">
          <div
            className="grid gap-0.5 bg-muted/60 p-4 rounded-xl"
            style={{
              gridTemplateColumns: `repeat(${gridSize}, 50px)`,
            }}
          >
            {userGrid.map((row, rIdx) =>
              row.map((cell, cIdx) => {
                const black = isBlackCell(rIdx, cIdx);
                const active = isActiveCell(rIdx, cIdx);
                const correct =
                  cell.toUpperCase() ===
                  grid[rIdx][cIdx].toUpperCase();

                return (
                  <div key={`${rIdx}-${cIdx}`} className="relative">
                    {black ? (
                      <div className="w-12 h-12 bg-black" />
                    ) : (
                      <Input
                        maxLength={1}
                        value={cell}
                        disabled={!active || showResult}
                        onChange={e =>
                          handleStudentChange(
                            rIdx,
                            cIdx,
                            e.target.value
                          )
                        }
                        className={cn(
                          "w-12 h-12 text-center text-xl font-bold uppercase",
                          showResult && correct && "bg-green-100 border-green-400",
                          showResult && !correct && "bg-red-100 border-red-400"
                        )}
                      />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* CLUES */}
        <div className="space-y-6">
          <div>
            <h3 className="font-semibold">Across</h3>
            {clues.across.map(c => (
              <p key={c.number}>
                {c.number}. {c.clue}
              </p>
            ))}
          </div>

          <div>
            <h3 className="font-semibold">Down</h3>
            {clues.down.map(c => (
              <p key={c.number}>
                {c.number}. {c.clue}
              </p>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}